/**
 * Section Import Modal
 * Import sections from previous reports with selective import options
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, Button, Input, Select, Card, Badge } from '../UI';
import { Search, FileText, Import, Loader2, AlertCircle } from 'lucide-react';
import { listReports, getReportSections, type Report, type ReportSection, type ImportSectionsRequest } from '../../api/reports';
import { useWorkspace } from '../../hooks/useWorkspace';

// Helper function to calculate word count
function calculateWordCount(text: string | null | undefined): number {
  if (!text) return 0;
  // Simple word count - split on whitespace and filter empty strings
  const words = text.split(/\s+/).filter((w) => w.length > 0);
  return words.length;
}

interface SectionImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetReportId: string;
  projectId: string;
  onImport: (request: ImportSectionsRequest) => void;
}

export function SectionImportModal({
  isOpen,
  onClose,
  targetReportId,
  projectId,
  onImport,
}: SectionImportModalProps) {
  const { projectId: workspaceProjectId } = useWorkspace();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReportId, setSelectedReportId] = useState<string>('');
  const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set());
  const [importModes, setImportModes] = useState<Record<string, 'copy_content' | 'regenerate_content'>>({});
  const [copyCitations, setCopyCitations] = useState<Record<string, boolean>>({});
  const [newSectionNames, setNewSectionNames] = useState<Record<string, string>>({});
  const [position, setPosition] = useState<number | undefined>(undefined);

  // Fetch reports for selection
  const { data: reports, isLoading: reportsLoading } = useQuery({
    queryKey: ['reports', projectId || workspaceProjectId],
    queryFn: () => listReports(projectId || workspaceProjectId || ''),
    enabled: isOpen && !!(projectId || workspaceProjectId),
  });

  // Fetch sections from selected report
  const { data: sectionsData, isLoading: sectionsLoading } = useQuery({
    queryKey: ['report-sections', selectedReportId],
    queryFn: () => getReportSections(selectedReportId, { includeCustom: true, orderBy: 'display_order' }),
    enabled: isOpen && !!selectedReportId,
  });

  const availableSections = sectionsData?.sections || [];
  const filteredReports = reports?.filter((r: Report) => 
    r.id !== targetReportId && // Exclude current report
    (searchQuery === '' || 
     r.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
     r.name?.toLowerCase().includes(searchQuery.toLowerCase()))
  ) || [];
  
  // Calculate total word count for selected sections
  const totalWordCount = useMemo(() => {
    return Array.from(selectedSections).reduce((total, sectionId) => {
      const section = availableSections.find((s: ReportSection) => s.id === sectionId);
      if (section) {
        return total + calculateWordCount(section.answer);
      }
      return total;
    }, 0);
  }, [selectedSections, availableSections]);

  const handleSectionToggle = (sectionId: string) => {
    const newSelected = new Set(selectedSections);
    if (newSelected.has(sectionId)) {
      newSelected.delete(sectionId);
      // Clean up related state
      const newModes = { ...importModes };
      const newCitations = { ...copyCitations };
      const newNames = { ...newSectionNames };
      delete newModes[sectionId];
      delete newCitations[sectionId];
      delete newNames[sectionId];
      setImportModes(newModes);
      setCopyCitations(newCitations);
      setNewSectionNames(newNames);
    } else {
      newSelected.add(sectionId);
      // Set defaults
      setImportModes({ ...importModes, [sectionId]: 'copy_content' });
      setCopyCitations({ ...copyCitations, [sectionId]: true });
    }
    setSelectedSections(newSelected);
  };

  const handleImport = () => {
    if (selectedSections.size === 0) {
      alert('Please select at least one section to import');
      return;
    }

    if (!selectedReportId) {
      alert('Please select a source report');
      return;
    }

    // Sort selected sections by their display_order in source report to preserve order
    const sortedSectionIds = Array.from(selectedSections).sort((a, b) => {
      const sectionA = availableSections.find((s: ReportSection) => s.id === a);
      const sectionB = availableSections.find((s: ReportSection) => s.id === b);
      const orderA = sectionA?.display_order ?? 0;
      const orderB = sectionB?.display_order ?? 0;
      return orderA - orderB;
    });
    
    const sectionsToImport = sortedSectionIds.map((sectionId) => {
      const section = availableSections.find((s: ReportSection) => s.id === sectionId);
      return {
        section_id: sectionId,
        import_mode: importModes[sectionId] || 'copy_content',
        copy_citations: copyCitations[sectionId] ?? true,
        new_section_name: newSectionNames[sectionId] || undefined,
      };
    });

    onImport({
      source_report_id: selectedReportId,
      sections_to_import: sectionsToImport,
      position: position,
    });
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedReportId('');
    setSelectedSections(new Set());
    setImportModes({});
    setCopyCitations({});
    setNewSectionNames({});
    setPosition(undefined);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Sections from Previous Report"
      size="xl"
    >
      <div className="space-y-4">
        {/* Step 1: Select Source Report */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Step 1: Select Source Report
          </label>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search reports by client name or title..."
              className="pl-10"
            />
          </div>
          {reportsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <Select
              value={selectedReportId}
              onChange={(e) => setSelectedReportId(e.target.value)}
              options={filteredReports.map((r: Report) => ({
                value: r.id,
                label: `${r.name || r.client_name} (${r.report_type})`,
              }))}
              placeholder="Select a report to import from..."
            />
          )}
        </div>

        {/* Step 2: Select Sections */}
        {selectedReportId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Step 2: Select Sections to Import ({selectedSections.size} selected)
            </label>
            {sectionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : availableSections.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No sections found in this report</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {availableSections.map((section: ReportSection) => {
                  const isSelected = selectedSections.has(section.id);
                  return (
                    <Card
                      key={section.id}
                      className={`p-3 cursor-pointer transition-all ${
                        isSelected
                          ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                      onClick={() => handleSectionToggle(section.id)}
                    >
                      <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSectionToggle(section.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 bg-white dark:bg-gray-800"
                      />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{section.section_name}</h4>
                            {section.is_custom && (
                              <Badge className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 text-xs">
                                Custom
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-xs">
                              Pos: {section.display_order}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {calculateWordCount(section.answer)} words
                            </Badge>
                          </div>
                          {section.question && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                              {section.question}
                            </p>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Configure Import Options */}
        {selectedSections.size > 0 && (
          <div className="space-y-3 border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Step 3: Configure Import Options
            </label>
            {Array.from(selectedSections).map((sectionId) => {
              const section = availableSections.find((s: ReportSection) => s.id === sectionId);
              if (!section) return null;

              return (
                <Card key={sectionId} className="p-4">
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-medium mb-2">{section.section_name}</h4>
                      <Input
                        label="New Section Name (optional)"
                        value={newSectionNames[sectionId] || ''}
                        onChange={(e) =>
                          setNewSectionNames({ ...newSectionNames, [sectionId]: e.target.value })
                        }
                        placeholder="Leave empty to keep original name"
                      />
                    </div>
                    <Select
                      label="Import Mode"
                      value={importModes[sectionId] || 'copy_content'}
                      onChange={(e) =>
                        setImportModes({
                          ...importModes,
                          [sectionId]: e.target.value as 'copy_content' | 'regenerate_content',
                        })
                      }
                      options={[
                        {
                          value: 'copy_content',
                          label: 'Copy Content (preserve answer and citations)',
                        },
                        {
                          value: 'regenerate_content',
                          label: 'Regenerate Content (use RAG to generate new answer)',
                        },
                      ]}
                    />
                    {importModes[sectionId] === 'copy_content' && (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={copyCitations[sectionId] ?? true}
                          onChange={(e) =>
                            setCopyCitations({ ...copyCitations, [sectionId]: e.target.checked })
                          }
                          className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 bg-white dark:bg-gray-800"
                        />
                        <label className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                          Copy citations from source section
                        </label>
                      </div>
                    )}
                    {importModes[sectionId] === 'regenerate_content' && (
                      <div className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                        Citations will be automatically regenerated based on new content
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Position and Summary */}
        <div className="space-y-3 border-t pt-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Insert Position (optional)
              </label>
              <Input
                type="number"
                value={position?.toString() || ''}
                onChange={(e) => setPosition(e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="Leave empty to preserve positions from source report"
                min={0}
                className="w-48"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                If empty, sections will be inserted preserving their relative order from the source report
              </p>
            </div>
            {selectedSections.size > 0 && (
              <div className="text-right">
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Word Count</div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {totalWordCount.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedSections.size} section{selectedSections.size !== 1 ? 's' : ''}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={selectedSections.size === 0 || !selectedReportId}
          >
            <Import className="h-4 w-4 mr-2" />
            Import {selectedSections.size} Section{selectedSections.size !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
